import apiClient from './client';

/** X→A 能力契约注册表（SDD §V.4 / VI.4 / VI.8）API 客户端。 */

export type ContractStatus = 'draft' | 'published' | 'deprecated';
export type ContractVisibility = 'private' | 'public';

export interface CapabilityContract {
  id: string;
  tenant_id: string;
  name: string;
  display_name: string | null;
  description: string | null;
  /** 输入域：接受的输入 schema/字段说明 */
  input_domain: Record<string, unknown>;
  /** 输出域：输出结构/格式约定 */
  output_domain: Record<string, unknown>;
  /** 领域规则清单 */
  domain_rules: unknown[] | null;
  /** 护栏（SDD §VII.2 旋钮 schema 风格） */
  guardrails: Record<string, unknown> | null;
  /** 评测标准：可含 eval_suite_id 引用 + 达标阈值 */
  eval_criteria: Record<string, unknown> | null;
  /** 实现该契约的 Agent 绑定（可空） */
  agent_id: string | null;
  /** Agent 绑定摘要（读路径返回，供 B/X 发现消费） */
  agent_name: string | null;
  visibility: ContractVisibility;
  status: ContractStatus;
  version: number;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ContractPayload {
  name: string;
  display_name?: string;
  description?: string;
  input_domain: Record<string, unknown>;
  output_domain: Record<string, unknown>;
  domain_rules: unknown[];
  guardrails: Record<string, unknown>;
  eval_criteria: Record<string, unknown>;
  agent_id?: string | null;
  visibility?: ContractVisibility;
}

export interface ContractUpdatePayload {
  name?: string;
  display_name?: string;
  description?: string;
  input_domain?: Record<string, unknown>;
  output_domain?: Record<string, unknown>;
  domain_rules?: unknown[];
  guardrails?: Record<string, unknown>;
  eval_criteria?: Record<string, unknown>;
  agent_id?: string | null;
  visibility?: ContractVisibility;
  /** published 状态显式升版本修订（version+1） */
  bump_version?: boolean;
}

export interface ContractEvalResult {
  contract_id: string;
  agent_id: string;
  suite_id: string;
  total: number;
  passed: number;
  pass_rate: number;
  composite_score: number;
  dimensions: Record<string, number | null>;
  run_id: string;
  min_pass_rate: number | null;
  min_composite_score: number | null;
  met_pass_rate: boolean | null;
  met_composite: boolean | null;
  met: boolean | null;
  failure_summary: string;
}

export interface ContractListParams {
  status?: ContractStatus;
  visibility?: ContractVisibility;
  q?: string;
}

export interface ContractListResponse {
  total: number;
  items: CapabilityContract[];
}

/** Agent 绑定候选（GET /agents 返回数组，取 id/name） */
export interface AgentOption {
  id: string;
  name: string;
}

/** 内置评测套件（GET /eval/suites） */
export interface EvalSuiteOption {
  suite_id: string;
  name: string;
  kind: string;
  case_count: number;
}

export async function listContracts(params: ContractListParams = {}): Promise<ContractListResponse> {
  const res = await apiClient.get('/contracts', {
    params: {
      status: params.status || undefined,
      visibility: params.visibility || undefined,
      q: params.q?.trim() || undefined,
    },
  });
  return res.data;
}

export async function getContract(id: string): Promise<CapabilityContract> {
  const res = await apiClient.get(`/contracts/${id}`);
  return res.data;
}

export async function createContract(payload: ContractPayload): Promise<CapabilityContract> {
  const res = await apiClient.post('/contracts', payload);
  return res.data;
}

export async function updateContract(
  id: string,
  payload: ContractUpdatePayload
): Promise<{ contract: CapabilityContract; version_bumped: boolean }> {
  const res = await apiClient.patch(`/contracts/${id}`, payload);
  return res.data;
}

export async function deleteContract(id: string): Promise<void> {
  await apiClient.delete(`/contracts/${id}`);
}

export async function publishContract(id: string): Promise<CapabilityContract> {
  const res = await apiClient.post(`/contracts/${id}/publish`);
  return res.data;
}

export async function deprecateContract(id: string): Promise<CapabilityContract> {
  const res = await apiClient.post(`/contracts/${id}/deprecate`);
  return res.data;
}

export async function evaluateContract(id: string): Promise<ContractEvalResult> {
  const res = await apiClient.post(`/contracts/${id}/evaluate`);
  return res.data;
}

export async function listAgentOptions(): Promise<AgentOption[]> {
  const res = await apiClient.get('/agents');
  return res.data as AgentOption[];
}

export async function listEvalSuiteOptions(): Promise<EvalSuiteOption[]> {
  const res = await apiClient.get('/eval/suites');
  const data = res.data as { total: number; suites: EvalSuiteOption[] };
  return data.suites || [];
}
